import { ExportButton } from '@/common/buttons/ExportButton';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const LogFile = () => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const gridRef = useRef();
  const filterModelRef = useRef({});
  const countRef = useRef(null);

  const [fieldDefs, setFieldDefs] = useState();
  const [exportReq, setExportReq] = useState();

  const pluginCode = 'LOG_FILE_VIEWER_PLUGIN';
  const entity = 'LOG_FILE';

  useEffect(() => {
    const reqParam = {
      entity,
      viewerPluginCode: pluginCode,
      operation: 'FIELD_DEFS',
    };
    rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, entity).then((fieldDefs) => {
      const nextFieldDefs = [];
      fieldDefs.forEach((fieldDef) => {
        if (fieldDef.field === 'level') {
          nextFieldDefs.push({
            ...fieldDef,
            type: 'NONE',
            cellRenderer: 'loggerEffectiveCellRenderer',
          });
          return;
        }
        // The CSV carries the raw SLF4J pieces (messageTemplate + arguments); hide them and
        // synthesize a logMessage column whose renderer interpolates them per row.
        if (fieldDef.field === 'messageTemplate') {
          nextFieldDefs.push(
            {
              field: 'logMessage',
              displayName: 'Log Message',
              type: 'NONE',
              cellRenderer: 'logMessageCellRenderer',
              sortable: false,
              filter: false,
              minWidth: 520,
            },
            { ...fieldDef, hide: true },
          );
          return;
        }
        if (fieldDef.field === 'arguments') {
          nextFieldDefs.push({ ...fieldDef, hide: true });
          return;
        }
        nextFieldDefs.push(fieldDef);
      });
      setFieldDefs(nextFieldDefs);
    });
  }, [module]);

  const getGridApi = () => gridRef.current?.gridApiRef?.current;

  const getCurrentExpr = useCallback(() => {
    return ExprUtils.filterModelToExpr(filterModelRef.current, fieldDefs);
  }, [fieldDefs]);

  const buildDataSource = useCallback(() => {
    return {
      getRows: (params) => {
        filterModelRef.current = params.filterModel ?? {};
        const expr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

        const reqParam = {
          entity,
          viewerPluginCode: pluginCode,
          operation: 'ROWS',
          expr,
          rowsReq: {
            startRow: params.startRow,
            endRow: params.endRow,
            expr,
            sortModel: params.sortModel,
          },
        };

        buildAndSetExportReq(reqParam);

        const summaryReqParam = {
          entity,
          viewerPluginCode: pluginCode,
          operation: 'AGGREGATES',
          rowsReq: { expr },
        };

        Promise.all([
          rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, entity),
          rsrc.data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, entity),
        ])
          .then(([rows, summary]) => {
            const totalCount = summary?.count ?? 0;
            countRef.current = totalCount;
            params.successCallback(rows, totalCount);
            gridRef.current.setRowCount(totalCount);
            gridRef.current.resize();
          })
          .catch(() => {
            params.failCallback();
          });
      },
    };
  }, [entity, fieldDefs, module, pluginCode]);

  const handleRefresh = useCallback(() => {
    const api = getGridApi();
    if (!api) return;
    const oldCount = countRef.current ?? 0;
    const lastVisible = api.getLastDisplayedRowIndex();
    const wasAtBottom = oldCount > 0 && lastVisible >= oldCount - 1;
    const expr = getCurrentExpr();
    const summaryReqParam = {
      entity,
      viewerPluginCode: pluginCode,
      operation: 'AGGREGATES',
      rowsReq: { expr },
    };
    rsrc
      .data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, entity)
      .then((summary) => {
        const totalCount = summary?.count ?? 0;
        countRef.current = totalCount;
        api.setRowCount(totalCount, false);
        gridRef.current.setRowCount(totalCount);
        api.refreshInfiniteCache();
        if (wasAtBottom && totalCount > 0) {
          api.ensureIndexVisible(totalCount - 1, 'bottom');
        }
      })
      .catch(() => {
        api.refreshInfiniteCache();
        if (wasAtBottom && oldCount > 0) {
          api.ensureIndexVisible(oldCount - 1, 'bottom');
        }
      });
  }, [entity, getCurrentExpr, module, pluginCode, rsrc]);

  const handleGoToFirst = useCallback(() => {
    const api = getGridApi();
    if (!api) return;
    api.ensureIndexVisible(0, 'top');
  }, []);

  const handleGoToLast = useCallback(() => {
    const api = getGridApi();
    if (!api) return;
    const expr = getCurrentExpr();
    const summaryReqParam = {
      entity,
      viewerPluginCode: pluginCode,
      operation: 'AGGREGATES',
      rowsReq: { expr },
    };
    rsrc.data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, entity).then((summary) => {
      const count = summary?.count;
      if (count == null || count <= 0) return;
      countRef.current = count;
      api.setRowCount(count, false);
      api.ensureIndexVisible(count - 1, 'bottom');
    });
  }, [entity, getCurrentExpr, module, pluginCode, rsrc]);

  const onGridReady = () => {
    const dataSource = buildDataSource();
    gridRef.current.setDataSource(dataSource);
  };

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      name: 'Log File Export',
      module,
      entity,
      uiType: 'VIEWER',
      viewerPluginCode: pluginCode,
      viewerReqParam: reqParam,
      fieldDefs,
    };

    setExportReq(exportReq);
  };

  return (
    fieldDefs && (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Title icon={far.faListUl} title="Log File" />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton aria-label="Go to first row" onClick={handleGoToFirst}>
              <FontAwesomeIcon icon={fas.faArrowUp} />
            </IconButton>
            <IconButton aria-label="Go to last row" onClick={handleGoToLast}>
              <FontAwesomeIcon icon={fas.faArrowDown} />
            </IconButton>
            <IconButton aria-label="Refresh" onClick={handleRefresh}>
              <FontAwesomeIcon icon={fas.faRotate} />
            </IconButton>
            <ExportButton exportReq={exportReq} />
          </Box>
        </Box>
        <Grid ref={gridRef} gridOptions={{ fieldDefs }} events={{ onGridReady }} />
      </>
    )
  );
};
